/**
 * The credit-confirm modal and its cost block (PREC-4263). Prices are never typed here: every
 * `{{unit}}` and `{{credits}}` is interpolated from the shared constants in
 * `@signalist/shared-types` that the API charges, and `count` picks the plural form.
 */
export const enCreditConfirm = {
	total: "Total",
	balanceAfter: "Balance after",
	credits_one: "{{credits}} credit",
	credits_other: "{{credits}} credits",
	upToCredits_one: "up to {{credits}} credit",
	upToCredits_other: "up to {{credits}} credits",
	atLeastCredits_one: "at least {{credits}} credit",
	atLeastCredits_other: "at least {{credits}} credits",
	// A part whose cost the run decides (review S1, M2): never a number the dialog cannot promise.
	atMostCredits_one: "at most {{credits}} credit",
	atMostCredits_other: "at most {{credits}} credits",
	variable: "varies",
	variableTotal: "Depends on the run",
	knownPlusVariable_one: "{{credits}} credit + what the run decides",
	knownPlusVariable_other: "{{credits}} credits + what the run decides",
	requiredToStart: "Needed to start",
	balanceUnavailable: "Balance unavailable",
	balanceUnavailableHint:
		"Your balance could not be loaded. The action checks it before anything is charged.",
	insufficient:
		"Your balance of {{balance}} credits does not cover this. You need {{missing}} more.",
	topUp: "Top up",
	pleaseWait: "Please wait",
	plansAndBilling: "Plans & Billing",
	nothingToRun: "Nothing in this selection would be run.",
	quoteFailed: "The cost of this action could not be worked out: {{reason}}.",
	failed: "This did not go through: {{reason}}.",
	refusedNothingCharged: "Not started: {{reason}}. Nothing was charged.",
	selectedLeads_one: "{{count}} selected lead",
	selectedLeads_other: "{{count}} selected leads",
	email: {
		title_one: "Find email",
		title_other: "Find emails",
		body: "Find a validated email address for <b>{{target}}</b>.",
		confirm_one: "Find email",
		confirm_other: "Find emails for {{count}} leads",
		chargeNote_one: "You are charged only if we find one.",
		chargeNote_other: "You are charged only for the emails we find.",
		LOOKUP_one: "{{count}} lead · searched, {{unit}} credit per email found",
		LOOKUP_other: "{{count}} leads · searched, {{unit}} credit per email found",
		// The lead's job offer or Maps business is searched too, each charged when found (review M1).
		LOOKUP_ENTITIES_one:
			"{{count}} lead · {{lookups}} addresses searched (person, job offer or Maps business), {{unit}} credit per email found",
		LOOKUP_ENTITIES_other:
			"{{count}} leads · {{lookups}} addresses searched (person, job offer or Maps business), {{unit}} credit per email found",
		// The list lookup skips an address the person already holds too (PREC-4265).
		SKIPPED_one:
			"{{count}} lead · already has an email, none was found before, or it bounced: skipped",
		SKIPPED_other:
			"{{count}} leads · already have an email, none was found before, or it bounced: skipped",
		// Contacts' lookup also skips a valid address (isEmailLookupSettled, review S2).
		SETTLED_SKIPPED_one:
			"{{count}} lead · already has a valid email, or none was found before: skipped",
		SETTLED_SKIPPED_other:
			"{{count}} leads · already have a valid email, or none was found before: skipped",
	},
	phone: {
		title_one: "Find phone number",
		title_other: "Find phone numbers",
		body: "Find a phone number for <b>{{target}}</b>.",
		confirm_one: "Find phone number",
		confirm_other: "Find phone numbers for {{count}} leads",
		chargeNote:
			"Each lookup is charged {{unit}} credits when it starts and refunded if no number is found.",
		LOOKUP_one: "{{count}} lead · {{unit}} credits, refunded if none is found",
		LOOKUP_other: "{{count}} leads · {{unit}} credits each, refunded if none is found",
		SKIPPED_one: "{{count}} lead · no number found before: skipped",
		SKIPPED_other: "{{count}} leads · no number found before: skipped",
		SETTLED_SKIPPED_one:
			"{{count}} lead · already has a valid number, or none was found before: skipped",
		SETTLED_SKIPPED_other:
			"{{count}} leads · already have a valid number, or none was found before: skipped",
	},
	companyContact: {
		title: "Find company email & phone",
		body: "Find the company email and phone for <b>{{target}}</b>.",
		confirm_one: "Find contacts for {{count}} company",
		confirm_other: "Find contacts for {{count}} companies",
		// What ListCompanyContactService.getCompanyEmailAndPhone charges: a company the provider
		// returns a record for, with or without a usable email or phone (review S5).
		chargeNote_one:
			"A company is charged {{count}} credit when our data provider has a record of it, even if that record holds no usable email or phone. A company the provider does not know is free.",
		chargeNote_other:
			"A company is charged {{count}} credits when our data provider has a record of it, even if that record holds no usable email or phone. A company the provider does not know is free.",
		COMPANY_LOOKUP_one: "{{count}} company · {{unit}} credit if the provider has a record of it",
		COMPANY_LOOKUP_other:
			"{{count}} companies · {{unit}} credit each if the provider has a record of them",
		COMPANY_COMPLETE_one: "{{count}} company · already has email and phone: skipped",
		COMPANY_COMPLETE_other: "{{count}} companies · already have email and phone: skipped",
		NO_COMPANY_DOMAIN_one: "{{count}} lead · no company website: skipped",
		NO_COMPANY_DOMAIN_other: "{{count}} leads · no company website: skipped",
	},
	rescore: {
		title: "Retrigger lead scoring",
		body: "Score the <b>{{target}}</b> with your current lead scoring.",
		confirm_one: "Score {{count}} lead",
		confirm_other: "Score {{count}} leads",
		toggle: "Also rescore leads that are already scored",
		toggleHint: "Scores them again with the current lead scoring.",
		NOT_SCORED_one: "{{count}} lead · not scored yet, included",
		NOT_SCORED_other: "{{count}} leads · not scored yet, included",
		SCORED_one: "{{count}} lead · rescore, {{unit}} credits",
		SCORED_other: "{{count}} leads · rescore, {{unit}} credits each",
		SCORED_SKIPPED_one: "{{count}} lead · already scored: skipped",
		SCORED_SKIPPED_other: "{{count}} leads · already scored: skipped",
	},
	deepResearch: {
		title: "Deep Research",
		confirm_one: "Run on {{count}} lead",
		confirm_other: "Run on {{count}} leads",
		LEADS_one: "{{count}} lead · {{unit}} credit",
		LEADS_other: "{{count}} leads · {{unit}} credit each",
		LEADS_UNKNOWN: "Every lead the run covers · {{unit}} credit each",
		confirmUnknown: "Run Deep Research",
	},
	aiColumn: {
		LEADS_one: "{{count}} lead · {{unit}} credit",
		LEADS_other: "{{count}} leads · {{unit}} credit each",
		// "All" and "All including assigned" run on leads the list's tab counts leave out (review S1).
		LEADS_UNKNOWN: "Every lead the column runs on · {{unit}} credit each",
	},
	preview: {
		title: "Start preview",
		body_one: "Search <b>{{count}} company</b> to preview the leads this list would find.",
		body_other: "Search <b>{{count}} companies</b> to preview the leads this list would find.",
		confirm_one: "Start preview of {{count}} company",
		confirm_other: "Start preview of {{count}} companies",
		// A preview runs the list's search and lookups; PREVIEW_COMPANY_CREDITS is its start check (M2).
		SEARCH_one:
			"{{count}} company searched · charged for each company, person and result the search finds",
		SEARCH_other:
			"{{count}} companies searched · charged for each company, person and result the search finds",
		EMAIL: "Email lookup for each lead found · {{unit}} credit per email found",
		PHONE:
			"Phone lookup for each lead found · {{unit}} credits per lookup, refunded when none is found",
		COMPANY_CONTACT:
			"Company email & phone for each company · {{unit}} credit when our data provider has a record of it",
		chargeNote:
			"A preview runs this list's search and contact lookups on these companies and is charged for what they find, like a list run. It starts only if your balance covers the amount needed to start.",
	},
	previewExtend: {
		title: "Extend preview",
		body_one: "Search <b>{{count}} more company</b> from this list.",
		body_other: "Search <b>{{count}} more companies</b> from this list.",
		confirm_one: "Extend preview by {{count}} company",
		confirm_other: "Extend preview by {{count}} companies",
		chargeNote:
			"An extension searches the additional companies like a list run and is charged for what it finds. It starts only if your balance covers the amount needed to start.",
	},
	signalScan: {
		body_one: "Scan <b>{{count}} company</b> for signals now.",
		body_other: "Scan <b>{{count}} companies</b> for signals now.",
		confirm_one: "Scan {{count}} company",
		confirm_other: "Scan {{count}} companies",
		COMPANIES_one: "{{count}} company · {{unit}} credits",
		COMPANIES_other: "{{count}} companies · {{unit}} credits each",
	},
	/** The Enrich menu's subtitles (decision "Menu subtitles"). */
	menu: {
		email_one: "{{credits}} credit per email found",
		email_other: "{{credits}} credits per email found",
		phone_one: "{{credits}} credit per phone number found",
		phone_other: "{{credits}} credits per phone number found",
		companyContact_one: "{{credits}} credit per company",
		companyContact_other: "{{credits}} credits per company",
		rescore_one: "{{credits}} credit per rescored lead",
		rescore_other: "{{credits}} credits per rescored lead",
		deepResearch_one: "{{credits}} credit per lead",
		deepResearch_other: "{{credits}} credits per lead",
	},
};
